import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#externalArrowsCached = false;
    this.#externalPrevious = null;
    this.#externalNext = null;

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      if (this.infinite) {
        scroller.removeEventListener('wheel', this.#handleWheelBoundaryWrap);
        scroller.removeEventListener('touchstart', this.#handleTouchStart);
        scroller.removeEventListener('touchmove', this.#handleTouchMoveBoundaryWrap);
        scroller.removeEventListener('touchend', this.#handleTouchEnd);
        scroller.removeEventListener('touchcancel', this.#handleTouchEnd);
      }
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    if (this.#slidesObserver) {
      this.#slidesObserver.disconnect();
      this.#slidesObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const previousIndex = this.current;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // Ignore rapid next/prev clicks during transition to prevent erratic left-right jumping
    if (event != null && isAdjacentSlide && this.hasAttribute('transitioning')) return;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) {
        this.#disabled = false;
        return;
      }

      // Pause the MutationObserver during DOM reorder to prevent it from
      // resetting #visibleSlides, IntersectionObserver, and current index
      this.#slidesObserver?.disconnect();

      // Force slide content visible during DOM reorder so broken view-timelines
      // don't cause blank frames during the scroll animation
      this.setAttribute('reordering', '');

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Determine scroll direction based on requested vs actual index:
      // - requestedIndex < 0 means user clicked "previous" (wants to go left)
      // - requestedIndex > lastIndex means user clicked "next" (wants to go right)
      const goingBackward = requestedIndex < 0;
      const { scroller } = this.refs;
      const axis = this.#scroll.axis;
      const scrollProp = axis === 'x' ? 'scrollLeft' : 'scrollTop';

      // Update the LQIP background to the target slide's image during backward scroll
      let originalLqipBg = null;
      if (goingBackward) {
        const targetImg = targetSlide.querySelector('img');
        const targetLqip = targetSlide.querySelector('.product-media__lqip');
        let lqipUrl = null;

        // Try to get the LQIP background-image from the target slide
        if (targetLqip) {
          lqipUrl = getComputedStyle(targetLqip).backgroundImage;
        }
        // Fallback: use the main image src with a small width parameter
        if ((!lqipUrl || lqipUrl === 'none') && targetImg) {
          const src = targetImg.currentSrc || targetImg.src;
          if (src) {
            // Modify URL to get a low-quality version (Shopify CDN supports width parameter)
            const lqipSrc = src.replace(/&?width=\d+/, '').replace(/\?/, '?width=40&');
            lqipUrl = `url('${lqipSrc}')`;
          }
        }

        if (lqipUrl && lqipUrl !== 'none') {
          originalLqipBg = this.style.getPropertyValue('--container-lqip-bg');
          this.style.setProperty('--container-lqip-bg', lqipUrl);
        }
      }

      // Prevent snap-jank during the wrap animation.
      this.#programmaticScroll = true;
      this.#scroll.snap = false;

      if (goingBackward) {
        // Place target BEFORE current so we scroll left (visually)
        currentSlide.before(targetSlide);

        // Instantly jump scroller so target is off-screen to the left and current is visible
        this.#scroll.snap = false;
        scroller.style.scrollBehavior = 'auto';
        const slideWidth = axis === 'x' ? targetSlide.offsetWidth : targetSlide.offsetHeight;
        scroller[scrollProp] = slideWidth; // current slide is now in view

        // Force synchronous reflow so the scroll position is applied before we animate
        void scroller.offsetHeight;

        scroller.style.scrollBehavior = '';
      } else {
        // Place target AFTER current so we scroll right
        currentSlide.after(targetSlide);
      }

      this.#scroll.to(targetSlide, { instant: false });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        // If scroll end never fires (edge cases), force a restore after a short timeout
        const scrollDone = this.#scroll.finished;
        const fallbackDone = new Promise((resolve) => setTimeout(resolve, 800));
        await Promise.race([scrollDone, fallbackDone]);

        // Disable scroll-snap and scroll-behavior so the restore is a true instant jump
        this.#scroll.snap = false;
        const { scroller } = this.refs;
        const originalScrollBehavior = scroller.style.scrollBehavior;
        scroller.style.scrollBehavior = 'auto';

        // Restore DOM and scroll position in a single synchronous block to minimize glitch.
        // Using visibility:hidden briefly while we settle layout and reconnect view-timelines.
        scroller.style.visibility = 'hidden';

        placeholder.replaceWith(targetSlide);

        // Force all slides to re-establish their view-timeline after DOM reorder.
        // iOS Safari breaks scroll-driven animations (view-timeline) when elements
        // are moved in the DOM, leaving .slide__content stuck at opacity: 0.
        for (const slide of this.refs.slides ?? []) {
          slide.style.setProperty('view-timeline-name', 'none');
        }
        void scroller.offsetHeight; // synchronous reflow to apply 'none'

        for (const slide of this.refs.slides ?? []) {
          slide.style.removeProperty('view-timeline-name');
        }

        const axis = this.#scroll.axis;
        const maxScroll =
          (axis === 'x' ? scroller.scrollWidth - scroller.clientWidth : scroller.scrollHeight - scroller.clientHeight) || 0;
        scroller[`scroll${axis === 'x' ? 'Left' : 'Top'}`] = index === 0 ? 0 : maxScroll;

        this.#scroll.snap = true;
        this.#programmaticScroll = false;
        scroller.style.scrollBehavior = originalScrollBehavior;
        scroller.style.visibility = '';
        this.removeAttribute('reordering');
        this.#disabled = false;

        // Restore the original LQIP background if we changed it
        if (originalLqipBg !== null) {
          if (originalLqipBg) {
            this.style.setProperty('--container-lqip-bg', originalLqipBg);
          } else {
            this.style.removeProperty('--container-lqip-bg');
          }
        }

        // Reconnect the MutationObserver now that DOM is restored
        if (this.#slidesObserver) {
          this.#slidesObserver.observe(this.refs.scroller, { childList: true });
        }

        // Force progress bar and other UI to sync (scroll events were suppressed while disabled)
        const targetIndex = index;
        this.current = targetIndex;
        requestAnimationFrame(() => {
          this.#updateCardGalleryProgress(targetIndex, this.slides?.length ?? 0);
        });
      });

      // The DOM reorder block handles its own scrolling, aria-hidden, current index,
      // and event dispatch in the microtask above — skip the code below to avoid
      // a conflicting double scroll that resets the first scroll's promise.
      const reorderSlide = slides[index];
      if (!reorderSlide) return;
      reorderSlide.setAttribute('aria-hidden', 'false');
      this.current = this.slides?.indexOf(reorderSlide) || 0;
      this.#centerSelectedThumbnail(index, 'smooth');
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: event != null,
          trigger: 'select',
          slide: reorderSlide,
          id: reorderSlide.getAttribute('slide-id'),
        })
      );
      return;
    }

    const slide = slides[index];
    if (!slide) return;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#programmaticScroll = true;
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.#preloadGalleryImagesOnInteraction();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.#preloadGalleryImagesOnInteraction();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Preload all images in the gallery when the user first interacts with arrows.
   * Only runs for card-galleries, once per slideshow. Reduces blank frames when
   * navigating to lazy-loaded slides.
   */
  #preloadGalleryImagesOnInteraction() {
    if (this.#hasPreloadedGalleryImages) return;
    if (!this.closest('.card-gallery')) return;
    this.#hasPreloadedGalleryImages = true;

    const imgs = this.querySelectorAll('img[src]');
    for (const img of imgs) {
      const url = img.currentSrc || img.getAttribute('src');
      if (url && !url.startsWith('data:')) {
        const preload = new Image();
        preload.src = url;
      }
    }
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  /** @type {boolean} */
  #hasPreloadedGalleryImages = false;

  /** @type {HTMLButtonElement | null} */
  #externalPrevious = null;

  /** @type {HTMLButtonElement | null} */
  #externalNext = null;

  /** @type {boolean} */
  #externalArrowsCached = false;

  get current() {
    return this.#current;
  }

  /**
   * Queries and caches external arrow buttons that target this slideshow by ID.
   * @returns {{ previous: HTMLButtonElement | null, next: HTMLButtonElement | null }}
   */
  #getExternalArrows() {
    if (!this.#externalArrowsCached && this.id) {
      this.#externalPrevious = document.querySelector(
        `[data-slideshow-control="${this.id}"][data-direction="previous"]`
      );
      this.#externalNext = document.querySelector(
        `[data-slideshow-control="${this.id}"][data-direction="next"]`
      );
      this.#externalArrowsCached = true;
    }
    return { previous: this.#externalPrevious, next: this.#externalNext };
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    thumbnails?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    // Dots are plain buttons (no tablist), so aria-selected isn't allowed; aria-current marks the active one.
    dots?.forEach((el, i) => el.setAttribute('aria-current', `${i === value}`));

    // Count only REAL slides. The `slides` getter excludes `[hidden]` slots
    // (e.g. surplus SSR carousel slides a Constructor pod collapsed after it
    // loaded fewer items). Using `refs.slides` here counted the hidden slots,
    // so `atEnd` never tripped → the next arrow stayed enabled with nothing
    // real to scroll to.
    const slideCount = this.slides?.length ?? 0;
    const atStart = Boolean(!this.infinite && value === 0);
    const atEnd = Boolean(!this.infinite && this.nextIndex >= slideCount);

    if (previous) previous.disabled = atStart;
    if (next) next.disabled = atEnd;

    const external = this.#getExternalArrows();
    if (external.previous) external.previous.disabled = atStart;
    if (external.next) external.next.disabled = atEnd;

    this.#updateCardGalleryProgress(value, this.slides?.length ?? 0);
  }

  /**
   * Update the card-gallery progress bar when inside .card-gallery (mobile only).
   *
   * @param {number} index - The current slide index.
   * @param {number} total - The total number of slides.
   * @returns {void}
   */
  #updateCardGalleryProgress(index, total) {
    if (!total || total <= 1) return;
    const bar = this.closest('.card-gallery')?.querySelector('.card-gallery__progress');
    if (!bar) return;

    const fill = bar.querySelector('.card-gallery__progress-fill');
    if (!fill) return;

    const progress = (index + 1) / total;
    fill.style.setProperty('--progress', String(progress));
    bar.setAttribute('aria-valuenow', String(index + 1));
    bar.setAttribute('aria-valuemax', String(total));
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * Indicates a programmatic scroll is in progress — skip #sync() in #handleScroll.
   * @type {boolean}
   */
  #programmaticScroll = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * MutationObserver that watches for dynamically replaced slides
   * (e.g., recently-viewed section replacing placeholder slides with real products).
   * @type {MutationObserver | null}
   */
  #slidesObserver = null;

  /**
   * Debounce flag for #slidesObserver to batch rapid DOM changes.
   * @type {boolean}
   */
  #slidesObserverPending = false;

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Watch for dynamically replaced slides (e.g., recently-viewed replaces
    // placeholder slides after fetching product data). When child nodes change
    // in the scroller, re-setup the IntersectionObserver to track new slides.
    const { scroller } = this.refs;
    this.#slidesObserver = new MutationObserver(() => {
      if (this.#slidesObserverPending) return;
      this.#slidesObserverPending = true;
      queueMicrotask(() => {
        this.#slidesObserverPending = false;
        if (!this.isConnected || !this.refs.slides?.length) return;
        this.#visibleSlides = [];
        this.#setupIntersectionObserver();
        this.current = 0;
      });
    });
    this.#slidesObserver.observe(scroller, { childList: true });

    // Setup the scroll instance
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    if (this.infinite) {
      scroller.addEventListener('wheel', this.#handleWheelBoundaryWrap, { passive: false });
      scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      scroller.addEventListener('touchmove', this.#handleTouchMoveBoundaryWrap, { passive: false });
      scroller.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
      scroller.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true });
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /** @type {number | undefined} Touch start X for boundary wrap detection. */
  #touchStartX;

  /** @type {number} Timestamp of last boundary wrap to avoid rapid re-triggers. */
  #lastBoundaryWrapTime = 0;

  /** Minimum touch delta (px) before triggering boundary wrap. */
  static #BOUNDARY_WRAP_TOUCH_THRESHOLD = 40;

  /** Minimum wheel delta before triggering boundary wrap. */
  static #BOUNDARY_WRAP_WHEEL_THRESHOLD = 40;

  /** Cooldown (ms) after a boundary wrap before allowing another. */
  static #BOUNDARY_WRAP_COOLDOWN = 400;

  /**
   * Records touch start position for boundary wrap detection.
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    this.#touchStartX = event.touches[0]?.clientX;
  };

  /** Clears touch start when touch ends. */
  #handleTouchEnd = () => {
    this.#touchStartX = undefined;
  };

  /**
   * Intercepts touch at scroll boundary when infinite: at start scrolling left or at end scrolling right,
   * prevents default and wraps to previous/next. Required for mobile where native scroll is used.
   * @param {TouchEvent} event
   */
  #handleTouchMoveBoundaryWrap = (event) => {
    if (!this.infinite || this.#touchStartX == null || !this.#scroll) return;

    const now = performance.now();
    if (now - this.#lastBoundaryWrapTime < Slideshow.#BOUNDARY_WRAP_COOLDOWN) return;

    const { scroller } = this.refs;
    const { axis } = this.#scroll;
    const edge = axis === 'x' ? 'Left' : 'Top';
    const scroll = scroller[`scroll${edge}`];
    const maxScroll = edge === 'Left' ? scroller.scrollWidth - scroller.clientWidth : scroller.scrollHeight - scroller.clientHeight;

    const currentX = event.touches[0]?.clientX ?? 0;
    const deltaX = currentX - this.#touchStartX;

    const atStart = scroll <= 0;
    const atEnd = maxScroll <= 0 || scroll >= maxScroll - 1;

    const threshold = Slideshow.#BOUNDARY_WRAP_TOUCH_THRESHOLD;

    if (atStart && deltaX >= threshold) {
      event.preventDefault();
      this.#lastBoundaryWrapTime = now;
      this.previous();
      this.#touchStartX = currentX;
    } else if (atEnd && deltaX <= -threshold) {
      event.preventDefault();
      this.#lastBoundaryWrapTime = now;
      this.next();
      this.#touchStartX = currentX;
    }
  };

  /**
   * Intercepts wheel at scroll boundary when infinite: at start scrolling left or at end scrolling right,
   * prevents default and wraps to previous/next. Covers trackpad and mouse wheel.
   * @param {WheelEvent} event
   */
  #handleWheelBoundaryWrap = (event) => {
    if (!this.infinite || !this.#scroll) return;

    const now = performance.now();
    if (now - this.#lastBoundaryWrapTime < Slideshow.#BOUNDARY_WRAP_COOLDOWN) return;

    const { scroller } = this.refs;
    const { axis } = this.#scroll;
    const edge = axis === 'x' ? 'Left' : 'Top';
    const scroll = scroller[`scroll${edge}`];
    const maxScroll = edge === 'Left' ? scroller.scrollWidth - scroller.clientWidth : scroller.scrollHeight - scroller.clientHeight;

    const delta = axis === 'x' ? event.deltaX : event.deltaY;

    const atStart = scroll <= 0;
    const atEnd = maxScroll <= 0 || scroll >= maxScroll - 1;

    const threshold = Slideshow.#BOUNDARY_WRAP_WHEEL_THRESHOLD;

    if (atStart && delta <= -threshold) {
      event.preventDefault();
      this.#lastBoundaryWrapTime = now;
      this.previous();
    } else if (atEnd && delta >= threshold) {
      event.preventDefault();
      this.#lastBoundaryWrapTime = now;
      this.next();
    }
  };

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    if (this.#disabled || this.#programmaticScroll) return;

    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#programmaticScroll = false;
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    // Prevent outer carousel from intercepting clicks on inner slideshow controls
    const controlButton = event.target.closest('.slideshow-control');
    if (controlButton) {
      const controlSlideshow = controlButton.closest('slideshow-component');
      if (controlSlideshow && controlSlideshow !== this) return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      let newIndex = next + modifier;

      if (this.infinite) {
        if (newIndex < 0) {
          newIndex = slides.length - 1;
        } else if (newIndex > slides.length - 1) {
          newIndex = 0;
        }
      } else {
        newIndex = clamp(newIndex, 0, slides.length - 1);
      }

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.select(newIndex, undefined, { animate: true });

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Public re-sync for when the slide set / contents were mutated EXTERNALLY —
   * e.g. a Constructor pod swapping card content into existing slides, or
   * collapsing surplus SSR slots after loading fewer items. The internal
   * observers only react to slide add/remove and `ref` attribute changes, so a
   * content-only swap or a `[hidden]` toggle leaves the carousel stale (arrows
   * + length out of step with the real slides). Call this after the swap to
   * re-derive the visible-slide set, clamp the current index to the real slide
   * count, and re-apply the prev/next disabled state (internal + external
   * arrows). No-op for single-slide / not-yet-initialised slideshows.
   */
  refresh() {
    // Sync `refs.slides` to the current DOM before we read it. The base
    // Component ref-observer updates refs asynchronously (and its observe() is
    // deferred to requestIdleCallback), so slides appended immediately before
    // this call may not yet be in `refs`. `updatedCallback` (the SRA hook) does
    // a synchronous `#updateRefs()`, giving us an up-to-date slide set here.
    this.updatedCallback();

    // Upgrade path: a slideshow that initialised in the single-slide,
    // no-controls branch (`#setupSlideshowWithoutControls`) never built its
    // Scroller or observers — e.g. a Constructor composed card whose cloned
    // gallery was scrubbed to one primary slide. Once the secondary-gallery
    // region appends real slides, promote it to a full slideshow so hover /
    // `next()` can reach them. No-op while still a genuine single-slide gallery.
    if (!this.#scroll) {
      if ((this.slides?.length ?? 0) > 1) this.#setupSlideshow();
      return;
    }

    if (!this.refs.slides?.length) return;
    this.#visibleSlides = [];
    this.#setupIntersectionObserver();
    const count = this.slides?.length ?? 0;
    this.#current = Math.min(Math.max(0, this.#current), Math.max(0, count - 1));
    // Re-fire the `current` setter so arrow disabled-state + progress recompute
    // against the now-correct (filtered) slide count.
    this.current = this.#current;
    this.#updateControlsVisibility();
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        // Track count before processing; re-evaluate arrows only when count changes.
        const prevVisibleCount = this.#visibleSlides.length;

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();

        // Re-evaluate arrow disabled state when visible slide count changes.
        // Fixes arrows staying active after dynamic slide replacement (e.g. recently-viewed).
        if (this.#visibleSlides.length !== prevVisibleCount) {
          this.current = this.current;
        }
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden and tabindex based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);

        // Update tabindex on focusable elements within the slide
        const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
        focusableElements.forEach((element) => {
          if (isVisible) {
            // Remove tabindex to allow natural tab order, or restore original tabindex
            const originalTabindex = element.getAttribute('data-original-tabindex');
            if (originalTabindex) {
              element.setAttribute('tabindex', originalTabindex);
              element.removeAttribute('data-original-tabindex');
            } else {
              element.removeAttribute('tabindex');
            }
          } else {
            // Store original tabindex if it exists
            const currentTabindex = element.getAttribute('tabindex');
            if (currentTabindex && currentTabindex !== '-1') {
              element.setAttribute('data-original-tabindex', currentTabindex);
            }
            // Set tabindex="-1" to remove from tab order
            element.setAttribute('tabindex', '-1');
          }
        });
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
